import { useContext, useEffect, useReducer, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Grid,
  Switch,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Users as HeaderIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { CvsAccountLogo, LearnMoreLink } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { userContext } from '@contexts'
import { RootState } from '@redux/store'
import { showSnackbar } from 'functions'
import { ApprovalSwitch } from './components'
import { reducer, initialState, ActionType } from './reducer'
import * as css from './styles'

const {
  setAccountName,
  setAccountNameChanged,
  setSaving,
  setEmailSaving,
  setAllowApproval,
  setAllowLoginTracking,
} = ActionType

export const OrganizationSettings = () => {
  const classes = css.useStyles()
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.Auth.user)
  const isAdmin: boolean = user?.role === 'account_owner'
  const [alertEmails, setAlertEmails] = useState<string[]>([])
  const [emails, setEmails] = useState<string[]>([])
  const { account, features } = useSelector((state: RootState) => state.Auth)
  const {
    selectAndImportPhoto,
    updateOrgName,
    updateSettings,
    setLogo,
    updateAlertEmails,
    getAlertEmails,
  } = useContext<any>(userContext)
  const [state, dispatchState] = useReducer(reducer, initialState)
  const { accountName, accountNameChanged, saving, allowApproval } = state
  const { fetchUsers } = useContext<any>(userContext)

  const APPROVAL_ENABLED = Boolean(features?.approval)
  const USER_LOGIN_TRACKING_ENABLED = Boolean(features?.['user-login-tracking'])

  useEffect(() => {
    void updateAlertEmailsAsync()

    async function updateAlertEmailsAsync() {
      const alertEmailResponse = await getAlertEmails()
      setAlertEmails(alertEmailResponse.data)
    }
  }, [])

  useEffect(() => {
    const userData = fetchUsers()
    userData.then(rs => {
      const emailAddresses = rs.map(row => {
        return row.email
      })
      setEmails(emailAddresses)
    })
  }, [])

  useEffect(() => {
    dispatchState({ type: setAccountName, payload: account.name || '' })
    dispatchState({ type: setAllowApproval, payload: account.allow_approval || false })
    dispatchState({
      type: setAllowLoginTracking,
      payload: USER_LOGIN_TRACKING_ENABLED,
    })
  }, [])

  useEffect(() => {
    if (accountName !== account.name) {
      dispatchState({ type: setAccountNameChanged, payload: true })
    } else {
      dispatchState({ type: setAccountNameChanged, payload: false })
    }
  }, [account.name, accountName])

  const url = `${account.subdomain}.claravine.com`
  const accountNameText = account.name

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      data-test-page-id="admin"
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Organization settings`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Set up your organization's identifying features`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <css.Container>
        <css.Row>
          <Typography variant="h3" gutterBottom>
            {t`Logo`}
          </Typography>
          <Typography variant="body1">
            {t`Your organization logo is useful to quickly identify the Claravine Internal Organization.`}
            <br />
            {t`When provided, it is displayed in the navigation menu.`}
          </Typography>
        </css.Row>
        <css.ImageRow>
          <css.ChangeImageButton type="button" onClick={handleChangeLogo}>
            <CvsAccountLogo
              account={account}
              style={{
                margin: 0,
                width: '100%',
                height: '100%',
              }}
            />
            <css.LogoOverlay>{t`Change`}</css.LogoOverlay>
          </css.ChangeImageButton>
          <div>
            <Typography variant="h4" gutterBottom>
              {t`Guidlines`}
            </Typography>
            <Typography variant="body1">
              {t`Here are a few tips for choosing an image that will display properly in Claravine:`}
            </Typography>
            <ul>
              <Typography variant="body1">
                <li>{t`Use a square image`}</li>
              </Typography>
              <Typography variant="body1">
                <li>{t`Use an image that is least 200 x 200 pixels`}</li>
              </Typography>
              <Typography variant="body1">
                <li>{t`Use an image with a solid background`}</li>
              </Typography>
            </ul>
          </div>
        </css.ImageRow>
        <css.Row>
          <Typography variant="h3" gutterBottom>
            {t`Name & URL`}
          </Typography>
          <Typography variant="body1">
            <Trans>
              Your current organization name is <b>{accountNameText}</b> and your URL is{' '}
              <b>{url}</b>
            </Trans>
          </Typography>
        </css.Row>
        <css.Row>
          <css.InputContainer>
            <TextField
              css={css.input}
              value={accountName}
              onChange={handleChangeAccountName}
              disabled={saving}
              fullWidth
              id="outlined-basic"
              variant="outlined"
              label={t`Organization name`}
            />
            <Button
              onClick={handleSave}
              disabled={saving || !accountNameChanged}
              color="secondary"
              variant="contained"
            >
              {t`Save`}
            </Button>
          </css.InputContainer>
        </css.Row>
        {/* Start of content controlled by 'approval' feature flag */}
        {APPROVAL_ENABLED && (
          <css.NoBMarginRow>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content">
                <Typography variant="h6">{t`Basic settings`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ApprovalSwitch
                  allowApproval={allowApproval}
                  handleToggleApproval={handleToggleApproval}
                />
              </AccordionDetails>
            </Accordion>
          </css.NoBMarginRow>
        )}
        {/* End of content controlled by 'approval' feature flag */}
        {USER_LOGIN_TRACKING_ENABLED && isAdmin && (
          <css.NoTMarginRow>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content">
                <Typography variant="h6">{t`Advanced settings`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container justifyContent="flex-start" alignItems="center">
                  <Typography>
                    {t`Get notified when unusual user login activity occurs&nbsp;`}
                    <LearnMoreLink href="https://google.com" />
                  </Typography>
                </Grid>
                <Grid container justifyContent="flex-end" alignItems="center">
                  <Switch defaultChecked size="medium" color="primary" />
                </Grid>
              </AccordionDetails>
              <AccordionDetails>
                <Autocomplete
                  value={alertEmails}
                  multiple
                  id="monitor-alert-email"
                  fullWidth
                  onChange={handleChangeAlertEmails}
                  freeSolo
                  options={emails}
                  getOptionLabel={option => option}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...props } = getTagProps({ index })
                      return <Chip key={key} label={option} {...props} />
                    })
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t`Send email alerts to:`}
                      onKeyDown={e => {
                        if (e.keyCode === 13 && (e.target as HTMLInputElement).value) {
                          setEmails(prev => prev.concat((e.target as HTMLInputElement).value))
                        }
                      }}
                    />
                  )}
                />
              </AccordionDetails>
            </Accordion>
          </css.NoTMarginRow>
        )}
      </css.Container>
    </motion.div>
  )

  function handleChangeLogo() {
    selectAndImportPhoto('logo').then(data => setLogo(data))
  }

  function handleChangeAccountName(e) {
    dispatchState({ type: setAccountName, payload: e.target.value })
  }

  function handleChangeAlertEmails(_, newValue) {
    dispatchState({ type: setEmailSaving, payload: false })
    setAlertEmails(newValue)
    void saveAlertEmails(newValue)
  }

  function handleSave() {
    dispatchState({ type: setSaving, payload: true })
    updateOrgName(accountName)
      .then(() => {
        dispatchState({ type: setSaving, payload: false })
        dispatch(
          showSnackbar({
            message: 'Organization Name saved successfully',
            type: 'SUCCESS',
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: 'Failed to save Organization Name',
            type: 'ERROR',
          }),
        )
      })
  }

  async function saveAlertEmails(emails) {
    try {
      await updateAlertEmails(emails)
      dispatch(
        showSnackbar({
          message: 'Email configuration saved successfully',
          type: 'SUCCESS',
        }),
      )
    } catch {
      dispatch(
        showSnackbar({
          message: 'Failed to save email configuration',
          type: 'ERROR',
        }),
      )
    }
  }

  function handleToggleApproval() {
    dispatchState({ type: setAllowApproval, payload: !allowApproval })
    updateSettings({ allow_approval: !allowApproval })
      .then(() => {
        dispatchState({ type: setSaving, payload: false })
        dispatch(
          showSnackbar({
            message: 'Organization Settings saved successfully',
            type: 'SUCCESS',
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: 'Failed to save Settings',
            type: 'ERROR',
          }),
        )
      })
  }
}
